interface MicButtonProps {
  onStart: () => void | Promise<void>;
  onStop: () => void;
  isRecording: boolean;
  /** Tapped, but the mic isn't capturing yet: amber, and a tap cancels. */
  isStarting?: boolean;
  disabled?: boolean;
}

/**
 * Tap-to-toggle mic button for SpeechRecognition.
 *
 * First tap → start (browser listens, auto-stops on silence).
 * Second tap → stop early if needed.
 * Spacebar hold-to-speak is handled separately in Chat.tsx.
 */
export default function MicButton({ onStart, onStop, isRecording, isStarting = false, disabled }: MicButtonProps) {
  const active = isRecording || isStarting;
  const handleClick = () => {
    if (active) {
      onStop();
    } else {
      void onStart();
    }
  };

  return (
    <button
      type="button"
      data-tour-id="mic"
      onClick={handleClick}
      disabled={disabled && !active}
      aria-label={isStarting ? "Getting the mic ready — tap to cancel" : isRecording ? "Stop listening" : "Tap to speak"}
      aria-pressed={active}
      className={`flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center transition-all ${
        isStarting
          ? "bg-amber-500 text-white animate-pulse"
          : isRecording
          ? "bg-scout-red text-white glow-red scale-110"
          : "text-white/40 hover:text-white/70 hover:bg-white/[0.06] disabled:opacity-30"
      }`}
    >
      {active ? (
        // Square stop icon while listening
        <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden="true">
          <rect x="6" y="6" width="12" height="12" rx="1" />
        </svg>
      ) : (
        // Mic icon
        <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden="true">
          <path d="M12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3zm-1 3.93A7 7 0 0 1 5 11H3a9 9 0 0 0 8 8.94V22h2v-2.06A9 9 0 0 0 21 11h-2a7 7 0 0 1-6 6.93z" />
        </svg>
      )}
    </button>
  );
}
